import { type FilterTag } from 'src/components/shared/appliedFilterTags/appliedFilterTags';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import { type KeysOfType } from 'src/types/utilities';

import { isNotEmpty } from './helpers';
import { createSearchRegExp } from './searchUtils';
import { sortBasedOnStrategy, SortStrategy } from './sortStrategies';

/**
 * sort table rows given sort column and direction
 */
export const sortTableRows = <T extends object>(tableRows: T[], sortColumn: keyof T, sortDirection: string) => {
  return tableRows.sort((a: T, b: T) => {
    const aValue = typeof a[sortColumn] !== 'number' ? JSON.stringify(a[sortColumn])?.toLowerCase() : a[sortColumn];
    const bValue = typeof b[sortColumn] !== 'number' ? JSON.stringify(b[sortColumn])?.toLowerCase() : b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === 'ascending' ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === 'ascending' ? 1 : -1;
    }

    return 0;
  });
};

/**
 * serialize value for filter
 */
const serializeValue = (value: any) => {
  if (typeof value === 'boolean') {
    return value === true ? 'Yes' : 'No';
  }
  return value;
};

/**
 * perform universal filter on all table columns given search field
 */
export const filterTableRows = <T extends object>(
  tableRows: T[],
  searchField: string,
  columnsToSkip = ['id'],
  columnsToConditionallySkip?: [string, (x: T) => boolean][],
) => {
  const searchRegExp = createSearchRegExp(searchField as string);
  return tableRows.filter((row) =>
    Object.entries(row)
      .map(([key, value]) => {
        return (
          !columnsToSkip.includes(key) &&
          !columnsToConditionallySkip?.some(([columnName, skipFunction]) => columnName === key && skipFunction(row)) &&
          value !== undefined &&
          searchRegExp.test(serializeValue(value))
        );
      })
      .reduce((prevValue: boolean, currValue: boolean) => prevValue || currValue),
  );
};

/**
 * paginate table rows given current page
 */
export const paginateTableRows = <T extends object>(
  tableRows: T[],
  currentPage: number,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
) => {
  const totalPages = Math.ceil(tableRows.length / pageSize);
  const startIndex = pageSize * (currentPage - 1);
  return {
    paginatedData: tableRows.slice(startIndex, startIndex + pageSize),
    totalPages,
  };
};

/**
 * combine sort and pagination into one function
 */
export const sortAndPaginateTableData = <T extends object>(
  tableRows: T[],
  sortColumn: keyof T,
  sortDirection: string,
  currentPage: number,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  sortStrategy = SortStrategy.DEFAULT,
) => {
  const sortedData = sortBasedOnStrategy(sortStrategy, tableRows, sortColumn, sortDirection);
  return paginateTableRows(sortedData, currentPage, pageSize);
};

/**
 * combine filter, sort and pagination into one function
 */
export const filterSortAndPaginateTableData = <T extends object>(
  tableRows: T[],
  searchField: string,
  sortColumn: keyof T,
  sortDirection: string,
  currentPage: number,
  columnsToSkip = ['id'],
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  sortStrategy = SortStrategy.DEFAULT,
  columnsToConditionallySkip?: [string, (x: any) => boolean][],
) => {
  const filteredData = filterTableRows([...tableRows], searchField, columnsToSkip, columnsToConditionallySkip);
  const sortedData = sortBasedOnStrategy(sortStrategy, filteredData, sortColumn, sortDirection);
  return paginateTableRows(sortedData, currentPage, pageSize);
};

/**
 * Filter a table by select options, like string[] or number[]
 */
export const filterByStrings = <T extends { [P in KeysOfType<T, string>]: string }>(
  rowData: T[],
  field: KeysOfType<T, string | undefined>,
  filterValue: string[],
) => {
  if (!filterValue.length) {
    return rowData;
  }
  const trimmedFilter = filterValue.map((filter) => filter.trim().toLowerCase());
  return rowData.filter((row) => !!row[field] && trimmedFilter.includes(row[field].trim().toLowerCase()));
};

/**
 * Filter a table by boolean options, like [true] or [true, false] (returns both true and
 * false). Note if a value is null or undefined, it will be treated as false.
 */
type FilterBool = boolean | null | undefined;
export const filterByBooleans = <T extends { [P in KeysOfType<T, FilterBool>]: FilterBool }>(
  rowData: T[],
  field: KeysOfType<T, FilterBool>,
  filterValue: boolean[],
) => {
  if (!filterValue.length) {
    return rowData;
  }
  return rowData.filter((row) => {
    const value = row[field];
    if (typeof value === 'boolean') {
      return filterValue.includes(value);
    } else if (value === null || value === undefined) {
      return filterValue.includes(false);
    }
  });
};

/**
 * Extract an array of unqiue values for any given field in a table. Used for populating filter dropdowns.
 */
export const extractAvailableFields = <T, V extends keyof T>(rowData: T[], field: V) =>
  Array.from(new Set(rowData.map((row) => row[field])))
    .filter((option) => isNotEmpty(option))
    .sort() as NonNullable<T[V]>[];

/**
 * get longest array out of an array of arrays
 */
export const getLongestArray = <T>(arrays: T[][]) => {
  const lengths = arrays.map((arr) => arr.length);
  return arrays[lengths.indexOf(Math.max.apply(null, lengths))];
};

/**
 * Create tags for filter tags
 */
export const createTags = <T extends { [P in keyof T]: string[] }>(
  filterOptions: T,
  field: keyof T,
  label: string,
): FilterTag<T>[] => {
  return filterOptions[field].map((value) => ({
    prefix: `${label}: `,
    value,
    optionName: field,
  }));
};

/**
 * Get updated filters when a user removes a tag on click
 */
export const getUpdatedFiltersOnTagRemoval = <T extends { [P in keyof T]: string[] }>(
  appliedFilterOptions: T,
  optionName: keyof T,
  valueToBeRemoved: unknown,
): T => {
  return {
    ...appliedFilterOptions,
    [optionName]: appliedFilterOptions[optionName].filter((value) => value !== valueToBeRemoved),
  };
};
